import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import HomeIcon from '@mui/icons-material/Home';
import RateReviewIcon from '@mui/icons-material/RateReview';
import ScienceIcon from '@mui/icons-material/Science';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import MapIcon from '@mui/icons-material/Map';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TimelineIcon from '@mui/icons-material/Timeline';

import { ROUTES } from '../router/constants/router-constants';

const DRAWER_WIDTH = 260;

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const navSections: NavSection[] = [
  {
    title: 'HOME',
    items: [
      {
        label: 'Home',
        path: ROUTES.HOME.path,
        icon: <HomeIcon />,
      },
    ],
  },
  {
    title: 'REQUESTS',
    items: [
      {
        label: 'Batch Rate',
        path: ROUTES.BATCH_RATE.path,
        icon: <RateReviewIcon />,
      },
      {
        label: 'AP Testing',
        path: ROUTES.AP_TESTING.path,
        icon: <ScienceIcon />,
      },
      {
        label: 'Rate Compare',
        path: ROUTES.RATE_COMPARE.path,
        icon: <CompareArrowsIcon />,
      },
      {
        label: 'Parcel Rate',
        path: ROUTES.PARCEL_RATE.path,
        icon: <LocalShippingIcon />,
      },
      {
        label: 'GEO Mileage',
        path: ROUTES.GEO_MILEAGE.path,
        icon: <MapIcon />,
      },
      {
        label: 'Invoice Audit Analysis',
        path: ROUTES.INVOICE_AUDIT.path,
        icon: <ReceiptIcon />,
      },
      {
        label: 'Custom Analysis',
        path: ROUTES.UPLOAD_ANALYZE.path,
        icon: <CloudUploadIcon />,
      },
      {
        label: 'Shipment Retrieval',
        path: ROUTES.PULL_DATA.path,
        icon: <CloudDownloadIcon />,
      },
    ],
  },
  {
    title: 'RESULTS',
    items: [
      {
        label: 'View Results',
        path: ROUTES.RESULTS.path,
        icon: <AssessmentIcon />,
      },
      {
        label: 'Runtime Hub',
        path: ROUTES.RUNTIME_HUB.path,
        icon: <TimelineIcon />,
      },
    ],
  },
];

export const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          backgroundColor: 'background.paper',
          color: 'text.primary',
          borderRightWidth: 1,
          borderRightStyle: 'solid',
          borderRightColor: 'divider',
        },
      }}
    >
      <Box sx={{ overflow: 'auto', pt: 2 }}>
        {navSections.map((section, sectionIndex) => (
          <Box key={section.title}>
            <Typography
              variant="caption"
              sx={{
                px: 3,
                py: 1.5,
                display: 'block',
                color: 'text.secondary',
                fontWeight: 600,
                fontSize: '0.7rem',
                letterSpacing: '0.5px',
              }}
            >
              {section.title}
            </Typography>
            <List sx={{ py: 0 }}>
              {section.items.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <ListItem key={item.path} disablePadding>
                    <ListItemButton
                      onClick={() => navigate(item.path)}
                      sx={{
                        py: 1.5,
                        px: 3,
                        backgroundColor: isActive ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                        borderLeft: isActive ? '3px solid #1976d2' : '3px solid transparent',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          color: isActive ? 'primary.main' : 'action.active',
                          minWidth: 40,
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: '0.875rem',
                          fontWeight: isActive ? 600 : 400,
                          color: isActive ? 'primary.main' : 'text.primary',
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
            {sectionIndex < navSections.length - 1 && (
              <Divider sx={{ my: 1 }} />
            )}
          </Box>
        ))}
      </Box>
    </Drawer>
  );
};

export const SIDEBAR_WIDTH = DRAWER_WIDTH;
